import {
  Scale,
  GraduationCap,
  Globe,
  Clock,
  Monitor,
  Users
} from "lucide-react";
import { courses } from "../data/mock";
import { EnquiryForm } from "../components/EnquiryForm";

const iconMap = { Scale, GraduationCap, Globe };

export default function CoursesPage() {
  return (
    <main className="pt-28 pb-32 bg-gradient-to-b from-[#0c0c0f] via-[#111113] to-[#0b0b0e] text-white space-y-36">
      {courses.map(course => (
        <CourseBlock key={course.id} course={course} />
      ))}
    </main>
  );
}

const CourseBlock = ({ course }) => {
  const Icon = iconMap[course.icon] || Scale;

  return (
    <section
      id={course.id}
      className="max-w-[1250px] mx-auto px-6 grid lg:grid-cols-3 gap-14"
    >
      {/* MAIN CONTENT */}
      <div className="lg:col-span-2 space-y-14">

        {/* HEADER */}
        <div className="flex gap-5 items-start">
          <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-orange-500/30 to-orange-600/10 border border-orange-400/30 flex items-center justify-center shadow-lg shadow-orange-500/10">
            <Icon className="text-[#F37021]" size={28} />
          </div>

          <div>
            <h2 className="text-4xl font-bold tracking-tight">
              {course.title}
            </h2>
            <p className="text-gray-400 mt-1 text-lg">
              {course.subtitle}
            </p>
          </div>
        </div>

        {/* DESCRIPTION */}
        <p className="text-gray-300 text-lg leading-relaxed max-w-3xl">
          {course.fullDesc}
        </p>

        {/* META */}
        <div className="grid grid-cols-3 gap-5">
          <Meta icon={Clock} label="Duration" value={course.duration} />
          <Meta icon={Monitor} label="Mode" value={course.mode} />
          <Meta icon={Users} label="Batch Size" value={course.batchSize} />
        </div>

        {/* CONTENT SECTIONS */}
        <ContentSection title="Course Highlights">
          <BulletList items={course.highlights} />
        </ContentSection>

        <ContentSection title="Program Overview">
          {Array.isArray(course.overview?.description)
            ? course.overview.description.map((d, i) => (
                <p key={i} className="text-gray-300">{d}</p>
              ))
            : <p className="text-gray-300">{course.overview?.description}</p>
          }

          {course.overview?.approach && (
            <p className="italic text-gray-400 mt-3">
              {course.overview.approach}
            </p>
          )}
        </ContentSection>

        {course.programFocus && (
          <FocusGrid title="Program Focus" data={course.programFocus} />
        )}

        {course.coreAcademicAreas && (
          <FocusGrid title="Core Academic Areas" data={course.coreAcademicAreas} />
        )}

        {course.targetAudience && (
          <ContentSection title="Who This Program Is For">
            <BulletList items={course.targetAudience} />
          </ContentSection>
        )}

        {course.teachingMethodology && (
          <ContentSection title="Teaching Methodology">
            <BulletList items={course.teachingMethodology} />
          </ContentSection>
        )}

        {course.offeringsToSchools && (
          <>
            <ContentSection title="Offerings To Schools">
              <BulletList items={course.offeringsToSchools} />
            </ContentSection>

            <ContentSection title="Integration Process">
              <BulletList items={course.integrationProcess} />
            </ContentSection>

            <ContentSection title="Why Schools Choose Us">
              <BulletList items={course.reasonsSchoolsChooseUs} />
            </ContentSection>
          </>
        )}
      </div>

      {/* ENQUIRY CARD */}
      <aside className="lg:col-span-1">
        <div className="sticky top-28 rounded-3xl bg-white/5 backdrop-blur-xl border border-white/10 p-8 shadow-xl shadow-black/40">

          <div className="h-1.5 w-20 bg-gradient-to-r from-orange-400 to-orange-600 rounded-full mb-6" />

          <h3 className="text-xl font-semibold mb-2">
            Interested in {course.title}?
          </h3>

          <p className="text-gray-400 text-sm mb-6">
            Get expert counselling & course roadmap
          </p>

          <EnquiryForm variant="compact" coursePreselect={course.id} />
        </div>
      </aside>
    </section>
  );
};

const Meta = ({ icon: Icon, label, value }) => (
  <div className="rounded-2xl bg-white/5 backdrop-blur border border-white/10 p-5 text-center hover:border-orange-500/40 transition">
    <Icon className="mx-auto mb-3 text-[#F37021]" size={20} />
    <div className="font-semibold text-lg">{value}</div>
    <div className="text-gray-400 text-sm">{label}</div>
  </div>
);

const ContentSection = ({ title, children }) => (
  <div className="space-y-5">
    <h3 className="text-2xl font-semibold text-[#F37021] tracking-tight">
      {title}
    </h3>
    {children}
  </div>
);

const BulletList = ({ items }) => (
  <ul className="grid sm:grid-cols-2 gap-y-2 gap-x-6 text-gray-300 list-disc pl-5">
    {items.map((item, i) => (
      <li key={i} className="leading-relaxed">
        {item}
      </li>
    ))}
  </ul>
);

const FocusGrid = ({ title, data }) => (
  <ContentSection title={title}>
    <div className="space-y-8">
      {Object.entries(data).map(([key, points]) => (
        <div
          key={key}
          className="rounded-2xl bg-white/4 border border-white/10 p-6 hover:border-orange-500/30 transition"
        >
          <h4 className="font-semibold text-lg mb-3">
            {key.replace(/([A-Z])/g, " $1")}
          </h4>
          <BulletList items={points} />
        </div>
      ))}
    </div>
  </ContentSection>
);
